import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import './LoginScreen.css';

const LoginScreen: React.FC = () => {
  const { state, requestOTP, verifyOTP, clearError } = useAuth();
  const { state: themeState, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [countdown, setCountdown] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Countdown timer for OTP resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [countdown]);

  // Clear error when component mounts or step changes
  useEffect(() => {
    clearError();
  }, [step, clearError]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    try {
      await requestOTP(email.trim());
      setStep('otp');
      setCountdown(60); // 60 second cooldown for resend
    } catch (error) {
      // Error is handled by the auth context
    }
  };

  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = otp.join('');
    if (otpString.length !== 6) return;

    try {
      await verifyOTP(email, otpString);
      // Success is handled by the auth context, user will be redirected
    } catch (error) {
      // Error is handled by the auth context
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;

    try {
      await requestOTP(email);
      setCountdown(60);
      setOtp(['', '', '', '', '', '']); // Clear OTP boxes
      otpRefs.current[0]?.focus();
    } catch (error) {
      // Error is handled by the auth context
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setOtp(['', '', '', '', '', '']);
    clearError();
  };

  const handleOTPChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(0, 1);
    
    if (digit) {
      const newOtp = [...otp];
      newOtp[index] = digit;
      setOtp(newOtp);

      // Move to next input if available
      if (index < 5 && otpRefs.current[index + 1]) {
        otpRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleOTPKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Move to previous input if current is empty
      otpRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOTPPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    
    if (pastedData.length > 0) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = pastedData[i] || '';
      }
      setOtp(newOtp);
      
      // Focus the next empty box or the last box
      const nextIndex = Math.min(pastedData.length, 5);
      otpRefs.current[nextIndex]?.focus();
    }
  };

  // Focus first OTP input when step changes to OTP
  useEffect(() => {
    if (step === 'otp') {
      setTimeout(() => {
        otpRefs.current[0]?.focus();
      }, 100);
    }
  }, [step]);

  return (
    <div className={`login-screen ${themeState.isDark ? 'dark' : 'light'}`}>
      {/* Theme Toggle Button */}
      <button
        className="theme-toggle-login"
        onClick={toggleTheme}
        title={themeState.isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        aria-label="Toggle theme"
      >
        {themeState.isDark ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="5"></circle>
            <line x1="12" y1="1" x2="12" y2="3"></line>
            <line x1="12" y1="21" x2="12" y2="23"></line>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
            <line x1="1" y1="12" x2="3" y2="12"></line>
            <line x1="21" y1="12" x2="23" y2="12"></line>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
          </svg>
        )}
      </button>

      {/* Background Video */}
      <video
        ref={videoRef}
        className="background-video"
        autoPlay
        muted
        loop
        playsInline
      >
        <source src="/video/intro.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Overlay */}
      <div className="login-overlay" />

      {/* Login Form Container */}
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1>decyx</h1>
            <p>
              {step === 'email' 
                ? 'Enter your email to sign in or create an account'
                : 'Enter the 6-digit code sent to your email'
              }
            </p>
          </div>

          {state.error && (
            <div className="error-message">
              {state.error}
            </div>
          )}

          {step === 'email' ? (
            <form onSubmit={handleEmailSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  disabled={state.loading}
                  autoFocus
                />
              </div>

              <button
                type="submit"
                className="submit-button"
                disabled={state.loading || !email.trim()}
              >
                {state.loading ? 'Sending Code...' : 'Continue'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleOTPSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="otp">Verification Code</label>
                <div className="otp-container">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => {
                        otpRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOTPChange(index, e.target.value)}
                      onKeyDown={(e) => handleOTPKeyDown(index, e)}
                      onPaste={handleOTPPaste}
                      disabled={state.loading}
                      className="otp-box"
                      aria-label={`Digit ${index + 1}`}
                    />
                  ))}
                </div>
                <div className="otp-help">
                  Code sent to {email}
                </div>
              </div>

              <button
                type="submit"
                className="submit-button"
                disabled={state.loading || otp.join('').length !== 6}
              >
                {state.loading ? 'Verifying...' : 'Verify & Continue'}
              </button>

              <div className="otp-actions">
                <button
                  type="button"
                  onClick={handleResendOTP}
                  className="link-button"
                  disabled={countdown > 0 || state.loading}
                >
                  {countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'}
                </button>

                <button
                  type="button"
                  onClick={handleBackToEmail}
                  className="link-button"
                  disabled={state.loading}
                >
                  Change Email
                </button>
              </div>
            </form>
          )}

          <div className="login-footer">
            <p>
              By continuing, you agree to our Terms of Service and Privacy Policy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
